import { Select } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { updateUserLanguage, getLanguageUpdateErrorMessage } from '@/api/user';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { useApiMutation } from '@/hooks/api';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import useLocale from '@/hooks/useLocale';
import { useSupportedLanguages } from '@/hooks/useSupportedLanguages';
import { useUrlState } from '@/hooks/useUrlState';
import { getApiErrorMessage } from '@/utils/handleApiError';
import { getLanguageFlagEmoji } from '@/utils/languageFlags';
import { getLocalStorageItem } from '@/utils/localStorage';
import { defaultFilterShowSearch } from '@/utils/selectHelpers';

interface LanguageSwitcherProps {
  variant?: string;
  updateUrl?: boolean;
}

const LanguageSwitcher = ({ updateUrl = false }: LanguageSwitcherProps) => {
  const [t] = useTranslation();
  const { showSuccess, showError, showWarning } = useApiFeedback();
  const { setParams } = useUrlState();
  const { sessionLocale: lang, setBrowserLocale } = useLocale();
  const [locale, setLocale] = useState(lang);
  const { user } = useCurrentUser();
  const { languages, isLoading } = useSupportedLanguages();

  const languageMutation = useApiMutation({
    mutationFn: (nextLang: string) => updateUserLanguage(nextLang),
    silent: true,
    onSuccess: (res) => {
      const updatedLang = res.default_language;
      showSuccess('LANGUAGE_UPDATED');
      // Ensure localStorage is set before reload
      localStorage.setItem('app_locale', updatedLang);
      localStorage.setItem('org_locale', updatedLang);
      // Reload the page to apply the new language across all components
      window.location.reload();
    },
    onError: (error: unknown) => {
      showError(
        `${t('LANGUAGE_UPDATE_FAILED')}: ${getLanguageUpdateErrorMessage(error) || getApiErrorMessage(error)}`,
      );
    },
  });

  const languageOptions = useMemo(
    () =>
      languages.map((language) => ({
        value: language.code,
        label: `${getLanguageFlagEmoji(language.code)} ${t(
          language.name,
          language.name,
        )}`,
      })),
    [languages, t],
  );

  const isAdminLoginAs = getLocalStorageItem(IS_ADMIN_KEY);

  useEffect(() => {
    if (isAdminLoginAs) {
      return;
    }

    const userLanguage = user?.default_language || lang;
    if (userLanguage && userLanguage !== locale) {
      setLocale(userLanguage);
      setBrowserLocale(userLanguage);
    }
  }, [user?.default_language, lang, locale, setBrowserLocale, isAdminLoginAs]);

  const handleLocale = (nextLang: string) => {
    setLocale(nextLang);
    setBrowserLocale(nextLang);

    if (updateUrl) {
      setParams({ lang: nextLang });
    }

    if (isAdminLoginAs) {
      showWarning('LANGUAGE_LOCAL_ONLY_ADMIN');
      return;
    }

    if (user) {
      languageMutation.mutate(nextLang);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Select
      loading={isLoading || languageMutation.isPending}
      disabled={
        isLoading || languages.length === 0 || languageMutation.isPending
      }
      showSearch={defaultFilterShowSearch}
      notFoundContent={null}
      onSelect={handleLocale}
      value={locale}
      options={languageOptions}
      placeholder={t('LOADING_LANGUAGES')}
      onClick={handleClick}
    />
  );
};

export default LanguageSwitcher;
